import { faExternalLinkSquare } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useField } from 'formik';
import React, { useState } from 'react';
import { BoldBodyTextTW } from 'tailwindComponents/bodyText/BodyTextTW';
import { TextFieldTW } from 'tailwindComponents/textInputTW/TextFieldTW';
import { Button } from 'ui-toolkit/components/Button/Button';
import { ISelectOption, SelectField } from '../../../../../../ui-toolkit';

interface ITrackingNumberProps {}

const shipmentTypeOptions: ISelectOption<string>[] = [
  { label: 'USPS', value: 'USPS' },
  { label: 'UPS', value: 'UPS' },
  { label: 'Certified Mail', value: 'USPS_CERTIFIED' },
  { label: 'FedEx', value: 'FEDEX' },
];

/** Readonly doesn't apply to this as when the check is 'Read Only' normally when it is after it is sent, but before we'd even have this tracking number yet */
/** Read Only is specifically when you have no permission. */
export const TrackingNumber: React.FC<ITrackingNumberProps> = () => {
  const [, , { setValue: setTrackingNumber }] = useField<string | undefined>(`value.trackingNumber`);
  const [{ value: trackingUrl }] = useField<string | undefined>(`value.trackingUrl`);
  const [{ value: shipmentType }] = useField<string | undefined>(`value.shipmentType`);

  const [isEditing, setIsEditing] = useState<boolean>(!!trackingUrl || !shipmentType);

  const onShipmentTypeChange = async (newShipmentType: ISelectOption<string>) => {
    if (newShipmentType.value === 'USPS') {
      await setTrackingNumber(undefined);
    }
  };

  return (
    <div className={'flex basis-full flex-col'}>
      <div className={'flex flex-row'}>
        <BoldBodyTextTW>Tracking Number</BoldBodyTextTW>
        <Button
          className={'ml-2'}
          intent={'ghost'}
          isInline
          onClick={() => setIsEditing((prev) => !prev)}
          data-test-id={'edit-button'}
        >
          {isEditing ? 'Cancel' : 'Edit'}
        </Button>
      </div>

      {isEditing && (
        <div className={'flex basis-full flex-row items-baseline gap-2'}>
          <SelectField
            className={'basis-1/4'}
            options={shipmentTypeOptions}
            name={`value.shipmentType`}
            placeholder={'Shipper...'}
            onChange={onShipmentTypeChange}
          />
          {shipmentType !== 'USPS' && (
            <TextFieldTW
              name={`value.trackingNumber`}
              placeholder={'Tracking Number'}
              data-test-id={'tracking-number'}
            />
          )}
        </div>
      )}
      <TrackingUrlComponent isEditing={isEditing} />
    </div>
  );
};

const TrackingUrlComponent: React.FC<{
  isEditing: boolean;
}> = ({ isEditing }) => {
  const [{ value: trackingNumber }, ,] = useField<string | undefined>(`value.trackingNumber`);
  const [{ value: trackingUrl }] = useField<string | undefined>(`value.trackingUrl`);
  const [{ value: shipmentType }] = useField<string | undefined>(`value.shipmentType`);

  if (isEditing) {
    return null;
  }

  return (
    <div data-test-id={'tracking-url'}>
      {trackingUrl && (
        <a href={trackingUrl} className={'text-blue-800 hover:underline'} target={'_blank'} rel="noreferrer">
          {trackingNumber} <FontAwesomeIcon icon={faExternalLinkSquare} />
        </a>
      )}
      {shipmentType === 'USPS' && <p>Shipped via USPS</p>}
    </div>
  );
};
